/**
 * New Password Challenge Form Component
 *
 * Form for handling new password challenges when users are required
 * to set a new password on first login or after admin reset:
 * - Pre-populated disabled email field
 * - New password with strength validation (8+ chars, lowercase, numbers)
 * - Password confirmation matching
 * - Error handling and user feedback
 */

import * as React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

interface NewPasswordChallengeFormProps {
  onSubmit: (password: string) => void;
  email: string;
  isSubmitting: boolean;
}

const NewPasswordChallengeForm: React.FC<NewPasswordChallengeFormProps> = ({
  onSubmit: onPasswordSubmit,
  email,
  isSubmitting,
}) => {
  const [submissionError, setSubmissionError] = React.useState<string>("");

  // Form configuration with comprehensive validation
  const formik = useFormik({
    initialValues: {
      email: email,
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/\d/, "Password must contain at least one number")
        .required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm Password is required"),
    }),
    onSubmit: (values) => {
      const { password } = values;

      try {
        // Clear any previous errors
        setSubmissionError("");
        // Call the parent component's function to complete the password challenge
        onPasswordSubmit(password);
      } catch (error: unknown) {
        console.error("Password challenge error:", error);

        let errorMessage = "Failed to set new password";
        if (error instanceof Error) {
          errorMessage = error.message;
        }

        setSubmissionError(errorMessage);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Typography component="h6" variant="h6">
            Please set your details below, and set a new password.
          </Typography>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            disabled
            fullWidth
            id="email"
            name="email"
            label="Email"
            variant="outlined"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            id="password"
            name="password"
            label="Password"
            type="password"
            variant="outlined"
            disabled={isSubmitting}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            id="confirmPassword"
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            variant="outlined"
            disabled={isSubmitting}
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.confirmPassword &&
              Boolean(formik.errors.confirmPassword)
            }
            helperText={
              formik.touched.confirmPassword && formik.errors.confirmPassword
            }
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress color="inherit" size={16} />
              ) : undefined
            }
          >
            {isSubmitting ? "Updating..." : "Update Details"}
          </Button>
        </Grid>

        {submissionError && (
          <Grid size={{ xs: 12 }}>
            {submissionError === "User already exists" ? (
              <Alert variant="outlined" severity="warning">
                {submissionError}. Please <a href="/login">login</a> instead.
              </Alert>
            ) : (
              <Alert variant="outlined" severity="error">
                {submissionError}
              </Alert>
            )}
          </Grid>
        )}
      </Grid>
    </form>
  );
};

export default NewPasswordChallengeForm;
